import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
  useTheme,
  alpha
} from '@mui/material';
import {
  CheckCircle,
  Star,
  TrendingUp,
  Security,
  Speed,
  Support
} from '@mui/icons-material';
import './common/Styles.css'
import { useNavigate } from 'react-router-dom';
import { CONST_ROUTE_PATH } from '../route/RouteConstants';
import {
  CONST_SITE_LABEL,
  PAYMENT_CURRENCY,
  STORAGE_KEYS,
  SUBSCRIPTION_STATUS,
  customArrayOperations,
  endOfDayForDateObj,
} from './common/Helper';
import DebugService from '../services/DebugService';
import SubscriptionApiService from '../services/SubscriptionApiService';
import { useAuth } from '../state/AuthContext';
import SnackbarNotification, { SNACKBAR_THEME } from './common/SnackbarNotification';
import ConfirmModal from './common/ConfirmModal';
import { useScopedLocalStorage } from '../hooks/useScopedLocalStorage';
import LogoLoader from './common/contentloader/LogoLoader';

/**
 * Pricing Component
 * @returns 
 */
export const Pricing = () => {
  const [planList, setPlanList] = useState([])
  const [disableButtonIdList, setDisableButtonIdList] = useState([])
  const [showErrorNotification, setShowErrorNotification] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [notificationErrorMessage, setNotificationErrorMessage] = useState('')
  const [paymentURL, setPaymentURL] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  // const [freePlan, setFreePlan] = useState({})

  const navigate = useNavigate()
  const {
    userDetails,
    loadingUserDetails,
    selectedAccount,
    selectedPlatform
    // refetchUserDetails
  } = useAuth()

  const { getScopedItem } = useScopedLocalStorage()

  /**
   * Buy/Start click handler
   * @param {*} plan 
   */
  const onBuy = (plan) => {
    // sessionStorage.setItem(STORAGE_KEYS.PLAN_PRICE, plan['price'])

    if (userDetails['userId'] && userDetails['userAccounts']) {
      // If logged in
      DebugService.log(`Pricing Component: Plan purchase initiated`)

      const userId = userDetails['userId'] ?? ''

      const requestBody = {
        userId,
        subscriptionId: plan['subscriptionId'],
        accountId: selectedAccount ?? ''
      }

      SubscriptionApiService.buySubscription(requestBody)
        .then(res => {
          DebugService.log(`Pricing Component: Plan purchase response`, res)

          if (res['data'] && res['data']['URL']) {

            if (res['data']['isTrialTaken']) {
              setShowConfirmModal(true)
              setPaymentURL(res['data']['URL'])
            } else {
              setDisableButtonIdList(prev => [
                ...prev,
                plan['subscriptionId']
              ])
              window.location.href = res['data']['URL']
            }

          } else {
            setShowErrorNotification(true)
            setNotificationErrorMessage(res['error'] ?? 'Something went wrong')
          }
        })
        .catch(error => {
          DebugService.log(`Pricing Component: Plan purchase failed`, error)
          setShowErrorNotification(true)
          setNotificationErrorMessage('Something went wrong. Please try again later.')
        })
    } else {

      // Navigate to sign-in if not logged in
      DebugService.log(`Pricing component: User is not logged in`, getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS))
      sessionStorage.setItem(STORAGE_KEYS.REDIRECT_URL, CONST_ROUTE_PATH.PRICING)
      navigate(CONST_ROUTE_PATH.SIGN_IN)
    }
  }

  /**
   * Get plan duration for display
   * @param {*} plan 
   * @returns 
   */
  const getDuration = (plan) => {
    // let duration = ` ${plan['days']} Day(s)`

    // if (plan['price'] === 0) {
    //   duration = `${duration} Free`
    // } else if (plan['days'] === 30 || plan['days'] === 31) {
    //   duration = ' per month'
    // }

    let duration = ` per month`

    if (plan['billingCycle'] > 1) {
      duration = ` per ${plan['billingCycle']} months`
    }

    return duration
  }

  /**
   * Purchased subscription handler for status of latest purchased plan
   * @param {*} purchasedPlan 
   */
  const handleSubscriptionStatus = (purchasedPlan) => {
    if (Object.keys(purchasedPlan).length > 0) {
      // if ([SUBSCRIPTION_STATUS.AUTHENTICATED, SUBSCRIPTION_STATUS.ACTIVE].includes(purchasedPlan['status'])) {
      if ([SUBSCRIPTION_STATUS.ACTIVE, SUBSCRIPTION_STATUS.TRIAL_ACTIVE].includes(purchasedPlan['status'])
        && (endOfDayForDateObj(new Date(purchasedPlan['currentEnd']))) >= (new Date())) {
        // Disable button
        setDisableButtonIdList(prev => [
          ...prev,
          purchasedPlan['subscriptionId']
        ])
      } else {
        setShowErrorNotification(true)
        setNotificationErrorMessage(`Either your plan has expired, or you haven't purchased one yet`)
      }
      // else if (['pending', 'halted'].includes(purchasedPlan['status'])) {
      // Redirect to payment page
      //   const paymentRequestBody = {
      //     businessName: CONST_BUSINESS_NAME,
      //     businessLogo: `${window.location.origin}/short-logo.png`,
      //     userId: userDetails['userId'],
      //     accountId: userDetails['userAccounts'] ? userDetails['userAccounts'][0]['accountId'] : '',
      //     userName: `${userDetails['firstname']} ${userDetails['lastname']}`,
      //     userEmail: userDetails['email'],
      //     userContact: '',
      //     address: '',
      //     subscriptionId: purchasedPlan['razorpaySubscriptionId'],
      //     redirectUrl: CONST_ROUTE_PATH.RESPONSE_HANDLER
      //   }

      //   const encodedDetails = btoa(JSON.stringify(paymentRequestBody))
      //   const customRoute = `${CONST_ROUTE_PATH.PAYMENT}/${encodedDetails}`

      //   navigate(customRoute)
      // }
    }
  }

  /**
   * Page load handler. Current activities - 
   * 
   * > Fetch master plan list
   * > Handle latest subscription status for logged in user
   */
  useEffect(() => {
    document.title = `${CONST_SITE_LABEL} | Pricing`

    // Fetch plan list
    setIsLoading(true)
    SubscriptionApiService.getPlanList(selectedPlatform)
      .then(res => {
        const data = res['data'] ?? []
        DebugService.log(`Pricing component: Plan list`, data)
        // const fPlan = data.filter(p => p['price'] === 0)
        // setFreePlan(fPlan && fPlan[0])
        setPlanList(data)
      })
      .catch(e => {
        DebugService.log(`Pricing component: Error caught while fetching plan list`, e)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [])

  useEffect(() => {
    if (loadingUserDetails === false && userDetails['userId']) {
      // Fetch purchased plan list
      const requestBody = {
        userId: userDetails['userId'],
        selectedAccount: selectedAccount
      }

      setIsLoading(true)
      SubscriptionApiService.getPurchasedPlanList(requestBody)
        .then(res => {
          const data = res['data'] ?? []
          DebugService.log(`Pricing component: Purchased Plan list`, data)

          if (data.length > 0) {
            const { latestRecord } = customArrayOperations(data, 'createdOn')

            // Handle latest subscription status
            if (latestRecord !== undefined || latestRecord !== null) {
              handleSubscriptionStatus(latestRecord)
            } else {
              DebugService.error(`Pricing component: Error caught while handling subscription based on latest purchased record`, latestRecord)
            }
          }
        })
        .catch(e => {
          DebugService.error(`Pricing component: Error caught while fetching purchased plan list`, e)
        })

      // Confirm and disable free plan
      if (userDetails['isTrialTaken']) {
        setDisableButtonIdList(prev => [
          ...prev,
          1
        ])
      }
      setIsLoading(false)
    }
  }, [userDetails, loadingUserDetails])

  /**
   * @description Buy subscription plan confirmation handler
   * @param {*} isConfirm 
   */
  const confirmationHandler = (isConfirm) => {
    if (isConfirm) {
      window.location.href = paymentURL
    }

    setShowConfirmModal(false)
  }

  const theme = useTheme();
  const activePlans = planList.filter(p => p['isActive']);

  return (
    <Container maxWidth="xl" sx={{ py: 4, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {isLoading && <LogoLoader />}
      
      {!isLoading && (
        <>
          {/* Hero Section - Compact */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography 
              variant="h4" 
              fontWeight={700} 
              sx={{ 
                mb: 2,
                background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Choose Your Perfect Plan
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mx: 'auto' }}>
              Unlock powerful FinOps capabilities with flexible pricing designed for every business size
            </Typography>
          </Box>

          {/* Pricing Cards - Horizontal Layout */}
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'stretch', justifyContent: 'center', py: 2 }}>
            <Grid container spacing={4} sx={{ maxWidth: 1200, alignItems: 'stretch' }}>
              {activePlans.map((plan, index) => {
                const isPopular = plan['labels']?.toLowerCase().includes('standard') || plan['labels']?.toLowerCase().includes('popular') || index === 1;
                const isCustom = plan['planType'] === 'CUSTOM';
                
                return (
                  <Grid item xs={12} md={activePlans.length <= 2 ? 6 : 4} key={plan['subscriptionId']} sx={{ display: 'flex' }}>
                    <Card 
                      elevation={isPopular ? 12 : 4}
                      sx={{
                        width: '100%',
                        position: 'relative',
                        border: isPopular ? `3px solid ${theme.palette.primary.main}` : '2px solid',
                        borderColor: isPopular ? 'primary.main' : 'grey.200',
                        borderRadius: 4,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        transform: isPopular ? 'scale(1.02)' : 'scale(1)',
                        background: isPopular 
                          ? 'linear-gradient(135deg, rgba(25, 118, 210, 0.02) 0%, rgba(255, 255, 255, 1) 100%)' 
                          : 'white',
                        '&:hover': {
                          transform: isPopular ? 'scale(1.05)' : 'scale(1.02)',
                          boxShadow: isPopular ? theme.shadows[20] : theme.shadows[8],
                          borderColor: isPopular ? 'primary.dark' : 'primary.light'
                        }
                      }}
                    >
                      {/* Popular Badge */}
                      {isPopular && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: -16,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 2
                          }}
                        >
                          {/* <Chip
                            icon={<Star sx={{ fontSize: 16 }} />}
                            label="Most Popular"
                            color="primary"
                            size="medium"
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.875rem',
                              px: 2,
                              py: 0.5,
                              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                              color: 'white',
                              '& .MuiChip-icon': {
                                color: 'white'
                              }
                            }}
                          /> */}
                        </Box>
                      )}
                      
                      <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column', pt: isPopular ? 5 : 4 }}>
                        {/* Plan Header */}
                        <Box sx={{ textAlign: 'center', mb: 3 }}>
                          <Typography 
                            variant="h6" 
                            fontWeight={600} 
                            color={isPopular ? 'primary.main' : 'text.primary'}
                            sx={{ mb: 1 }}
                          >
                            {plan['labels']}
                          </Typography>
                          
                          {isCustom ? (
                            <Box>
                              <Typography variant="h3" fontWeight={700} color="text.primary">
                                Custom
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Tailored Pricing
                              </Typography>
                            </Box>
                          ) : (
                            <Box>
                              <Typography variant="h3" fontWeight={700} color="text.primary">
                                {PAYMENT_CURRENCY[plan['currency']]['symbol'] ?? ''}{plan['price']}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {getDuration(plan)}
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        {/* Features List - Compact */}
                        <Box sx={{ flexGrow: 1, mb: 3 }}>
                          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, textAlign: 'center' }}>
                            What's Included
                          </Typography>
                          
                          <Stack spacing={1}>
                            {plan['details']?.['services']?.map((feature, i) => (
                              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckCircle 
                                  sx={{ 
                                    fontSize: 16, 
                                    color: isPopular ? 'primary.main' : 'success.main' 
                                  }} 
                                />
                                <Typography variant="body2" color="text.secondary">
                                  {feature}
                                </Typography>
                              </Box>
                            ))}
                            {/* {plan['details']?.['services']?.length > 4 && (
                              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', fontStyle: 'italic' }}>
                                +{plan['details']['services'].length - 4} more features
                              </Typography>
                            )} */}
                          </Stack>
                        </Box>

                        {/* Action Button */}
                        <Button
                          variant={isPopular ? 'contained' : 'outlined'}
                          size="large"
                          fullWidth
                          disabled={disableButtonIdList.includes(plan['subscriptionId'])}
                          onClick={() => isCustom ? navigate(CONST_ROUTE_PATH.SCHEDULE_MEETING) : onBuy(plan)}
                          sx={{
                            py: 1.5,
                            fontWeight: 600,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontSize: '1rem',
                            ...(isPopular && {
                              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)'
                              }
                            })
                          }}
                        >
                          {isCustom 
                            ? 'Request Demo' 
                            : plan['trialDays'] && plan['trialDays'] > 0 
                              ? `Start ${plan['trialDays']}-Day Free Trial` 
                              : 'Get Started'
                          }
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          {/* Trust Indicators - Compact Footer */}
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={3} 
              justifyContent="center" 
              alignItems="center"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Security color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Enterprise Security
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Speed color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Real-time Analytics
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Support color="primary" />
                <Typography variant="body2" color="text.secondary">
                  24/7 Support
                </Typography>
              </Box>
            </Stack>
          </Box>
        </>
      )}

      {/* Notifications and Modals */}
      {showErrorNotification && (
        <SnackbarNotification
          initialOpen={true}
          duration={7000}
          message={notificationErrorMessage}
          actionButtonName={'Ok'}
          theme={SNACKBAR_THEME.RED}
          yPosition={'top'}
          xPosition={'center'}
        />
      )}

      {showConfirmModal && (
        <ConfirmModal
          open={true}
          headingLabel={'Free Trial Already Used!'}
          message={[
            <Typography key="msg1" variant="body2" sx={{ mb: 2 }}>
              Your AWS account has already utilized the free trial offer. The free trial is limited to one use per account.
            </Typography>,
            <Typography key="msg2" variant="body2">
              You can proceed with the full subscription plan.
            </Typography>
          ]}
          continueBtnLabel={'Proceed'}
          cancelBtnLabel={'Close'}
          onActionTaken={(isConfirm) => confirmationHandler(isConfirm)}
        />
      )}
    </Container>
  )
}
